import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Button, CircularProgress, Divider, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import { alpha } from "@mui/material/styles";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import Close from "@mui/icons-material/Close";
import Search from "@mui/icons-material/Search";
import RestartAlt from "@mui/icons-material/RestartAlt";
import WarningAmber from "@mui/icons-material/WarningAmber";

// for theme alignment
const PRIMARY = "#0b3d87";
const profileFieldMinHeight = { xs: 52, sm: 54 };
const profileFieldMinWidth = 300;
const textFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    fontFamily: "Rubik, sans-serif",
    fontSize: { xs: "1.02rem", sm: "1.07rem" },
    borderRadius: "10px",
    backgroundColor: "#fafbff",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    minHeight: profileFieldMinHeight,
    minWidth: profileFieldMinWidth,
    "& fieldset": { borderColor: alpha(PRIMARY, 0.14) },
    "&:hover fieldset": { borderColor: alpha(PRIMARY, 0.35) },
    "&.Mui-focused fieldset": { borderColor: PRIMARY, borderWidth: 1 },
  },
  "& .MuiInputBase-input": { color: PRIMARY, py: { xs: 1.45, sm: 1.6 } },
  "& .MuiFormHelperText-root": { fontFamily: "Rubik, sans-serif" },
};

export const EMPTY_ADDRESS_FORM = {
  via: "",
  civico: "",
  comune: "",
  cap: "",
  provincia: "",
  regione: "",
  nazione: "",
};

// constants

export const ADDRESS_FORM_FIELD_KEYS = ["via", "civico", "comune", "cap", "provincia", "regione", "nazione"];

const OPTIONAL_CIVICO = new Set(["civico"]);

export const CIVICO_UNVERIFIED_TOOLTIP_IT =
  "Non abbiamo potuto verificare il numero civico. Lasciamo il valore che hai inserito, confermalo solo se sei sicuro che sia corretto.";

export const LOCATION_IQ_USER_MESSAGE_IT =
  "Servizio momentaneamente non disponibile. Ci scusiamo per il disagio, riprova più tardi.";

export const REGIONE_OPTIONAL_TOOLTIP_IT =
  "La regione è facoltativa: lasciala vuota per indirizzi fuori dall'Italia o quando non esiste un livello regionale. Se la includi sarà verificata.";

export const NAZIONE_TOOLTIP_IT =
  "Inserisci il nome dello stato/nazione in italiano.";

export const ADDRESS_SERVICE_CODES = {
  MISSING_API_KEY: "MISSING_API_KEY",
  LOCATION_IQ_UNAVAILABLE: "LOCATION_IQ_UNAVAILABLE",
  ABORTED: "ABORTED",
  INCOMPLETE_FORM: "INCOMPLETE_FORM",
  VALIDATION_FAILED: "VALIDATION_FAILED",
};

const LOCATIONIQ_SEARCH_URL = "https://api.locationiq.com/v1/search";
const GLOBAL_STREET_PREFIXES = [
  "boulevard", "esplanade", "promenade", "carretera", "piazzale", "avenida", "impasse", "passage",
  "terrace", "avenue", "straße", "strada", "vicolo", "piazza", "carrera", "camino", "chemin", "square",
  "straat", "allee", "alley", "drive", "place", "largo", "corso", "vialle", "viale", "court", "quai",
  "rua", "ulica", "road", "trau", "calle", "str.", "ter.", "dr.", "ln.", "ct.", "sq.", "pl.", "rd.",
  "st.", "wy.", "blvd", "lane", "str", "ter", "dr", "ln", "ct", "sq", "pl", "rd", "st", "wy", "via",
  "vico", "rue", "ave",
];

function stripLeadingStreetPrefix(text) {
  const lower = text.toLowerCase();
  for (const prefix of GLOBAL_STREET_PREFIXES) {
    if (!lower.startsWith(prefix)) continue;
    const rest = text.slice(prefix.length);
    if (rest.length > 0 && /\w/.test(rest[0])) continue;
    return rest.replace(/^[\s.,;-]+/, "").trim();
  }
  return text;
}

// logic

export function stripStreetPrefix(raw) {
  let t = String(raw ?? "").trim();
  let prev;
  do {
    prev = t;
    t = stripLeadingStreetPrefix(t);
  } while (t !== prev);
  return t;
}

function normalizeAddressString(s) {
  if (!s) return "";
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function bigrams(s) {
  const set = new Set();
  for (let i = 0; i < s.length - 1; i++) set.add(s[i] + s[i + 1]);
  return set;
}

function diceSimilarity(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const ba = bigrams(a);
  const bb = bigrams(b);
  let inter = 0;
  for (const g of ba) if (bb.has(g)) inter++;
  return (2 * inter) / (ba.size + bb.size);
}

// build the token pool
export function buildTokenPoolFromGeocodeHit(geocodeHit) {
  const tokens = new Set();
  const addr = geocodeHit?.address ?? {};
  for (const v of Object.values(addr)) {
    if (typeof v === "string" && v.trim()) tokens.add(v.trim());
  }
  const dn = geocodeHit?.display_name ?? "";
  for (const t of dn.split(",").map((x) => x.trim()).filter(Boolean)) tokens.add(t);
  return [...tokens];
}

// match the user's input to the token pool with a threshold score and returns the best match
export function matchUserValueToTokenPool(userValue, tokenPool, { viaMode = false } = {}) {
  const uNorm = normalizeAddressString(userValue);
  if (!uNorm) return null;
  const threshold = viaMode ? 0.55 : 0.45;
  let best = null;
  let bestScore = -1;
  for (const token of tokenPool) {
    const tNorm = normalizeAddressString(token);
    if (!tNorm) continue;
    if (uNorm === tNorm) return { token, score: 1 };
    if (tNorm.includes(uNorm) || uNorm.includes(tNorm)) {
      const score = 0.9;
      if (score > bestScore) {
        bestScore = score;
        best = token;
      }
      continue;
    }
    const score = diceSimilarity(uNorm, tNorm);
    if (score > bestScore) {
      bestScore = score;
      best = token;
    }
  }
  if (bestScore >= threshold) return { token: best, score: bestScore };
  return null;
}

export function isAddressFormComplete(form) {
  return ADDRESS_FORM_FIELD_KEYS.every((k) => {
    if (k === "regione") return true;
    return String(form[k] ?? "").trim().length > 0;
  });
}

function buildAllRejectedFields(form) {
  const fields = {};
  for (const key of ADDRESS_FORM_FIELD_KEYS) {
    const userVal = String(form[key] ?? "").trim();
    if (userVal) {
      fields[key] = { filled: true, found: false, normalized: "", score: 0, source: "geocode_error" };
    } else {
      fields[key] = { filled: false, found: false, normalized: "", score: 0, source: "empty" };
    }
  }
  return fields;
}

// check if all non-cap and non-civico fields match
function allNonCapNonCivicoFieldsMatch(fields, form) {
  for (const key of ADDRESS_FORM_FIELD_KEYS) {
    if (key === "cap" || key === "civico") continue;
    if (key === "regione" && !String(form?.regione ?? "").trim()) continue;
    const fr = fields[key];
    if (!fr?.filled || !fr?.found) return false;
  }
  return true;
}

function applyPostcodeFromHitIfEligible(geocodeHit, fields, form) {
  const capFr = fields.cap;
  if (!capFr?.filled || capFr.found) return fields;
  if (!allNonCapNonCivicoFieldsMatch(fields, form)) return fields;
  const postcode = String(geocodeHit?.address?.postcode ?? "").trim();
  if (!postcode) return fields;
  return {
    ...fields,
    cap: { filled: true, found: true, normalized: postcode, score: 1, source: "address.postcode" },
  };
}

function computeOverallValidity(fields, form) {
  return ADDRESS_FORM_FIELD_KEYS.every((key) => {
    if (key === "civico") return true;
    if (key === "regione" && !String(form?.regione ?? "").trim()) return true;
    const fr = fields[key];
    return Boolean(fr?.filled && fr?.found);
  });
}

export function validateGeocodeResultAgainstForm(geocodeHit, form) {
  const pool = buildTokenPoolFromGeocodeHit(geocodeHit);
  const fields = {};
  for (const key of ADDRESS_FORM_FIELD_KEYS) {
    const userVal = String(form[key] ?? "").trim();
    if (!userVal) {
      fields[key] = { filled: false, found: false, normalized: "", score: 0, source: "empty" };
      continue;
    }
    const match = matchUserValueToTokenPool(userVal, pool, { viaMode: key === "via" });
    if (match) {
      fields[key] = {
        filled: true,
        found: true,
        normalized: match.token,
        score: match.score,
        source: "token_pool",
      };
    } else if (OPTIONAL_CIVICO.has(key)) {
      fields[key] = { filled: true, found: false, normalized: "", score: 0, source: "not_found" };
    } else {
      fields[key] = { filled: true, found: false, normalized: "", score: 0, source: "not_found" };
    }
  }
  const fieldsWithPostcode = applyPostcodeFromHitIfEligible(geocodeHit, fields, form);
  const isValid = computeOverallValidity(fieldsWithPostcode, form);
  return { fields: fieldsWithPostcode, isValid, warnings: [] };
}

export function buildNormalizedFormFromFieldResults(form, fields) {
  const next = { ...form };
  for (const [key, fr] of Object.entries(fields)) {
    if (key === "civico") continue;
    if (fr.filled && fr.found && fr.normalized) next[key] = fr.normalized;
  }
  return next;
}

export function buildLocationIQSearchParams(form, { apiKey, language }) {
  const params = new URLSearchParams({
    key: apiKey,
    format: "json",
    addressdetails: "1",
    normalizeaddress: "1",
    normalizecity: "1",
    limit: "1",
    dedupe: "1",
    "accept-language": language,
  });
  const rawVia = String(form.via ?? "").trim();
  const viaForStreet = stripStreetPrefix(rawVia);
  const street = [viaForStreet, String(form.civico ?? "").trim()].filter(Boolean).join(" ");
  if (street) params.set("street", street);
  if (String(form.comune ?? "").trim()) params.set("city", String(form.comune).trim());
  if (String(form.cap ?? "").trim()) params.set("postalcode", String(form.cap).trim());
  if (String(form.provincia ?? "").trim()) params.set("county", String(form.provincia).trim());
  if (String(form.regione ?? "").trim()) params.set("state", String(form.regione).trim());
  if (String(form.nazione ?? "").trim()) params.set("country", String(form.nazione).trim());
  return params;
}

export function parseLocationIQSearchFirstHit(data) {
  if (Array.isArray(data) && data.length > 0) return data[0];
  if (data && typeof data === "object" && data.place_id != null) return data;
  return null;
}

function isLocationIQErrorPayload(data) {
  return data && typeof data === "object" && typeof data.error === "string" && data.error.trim().length > 0;
}

export async function validateAddressWithLocationIQ(form, options) {
  const { apiKey, language = "it", signal } = options ?? {};
  if (!String(apiKey ?? "").trim()) return { ok: false, code: ADDRESS_SERVICE_CODES.MISSING_API_KEY };
  if (!isAddressFormComplete(form)) return { ok: false, code: ADDRESS_SERVICE_CODES.INCOMPLETE_FORM };
  const params = buildLocationIQSearchParams(form, { apiKey, language });
  let response;
  try {
    response = await fetch(`${LOCATIONIQ_SEARCH_URL}?${params}`, { signal });
  } catch (e) {
    if (e?.name === "AbortError") return { ok: false, code: ADDRESS_SERVICE_CODES.ABORTED };
    return { ok: false, code: ADDRESS_SERVICE_CODES.LOCATION_IQ_UNAVAILABLE };
  }
  let data;
  try {
    data = await response.json();
  } catch {
    return { ok: false, code: ADDRESS_SERVICE_CODES.LOCATION_IQ_UNAVAILABLE };
  }
  if (isLocationIQErrorPayload(data)) {
    return { ok: false, code: ADDRESS_SERVICE_CODES.VALIDATION_FAILED, fields: buildAllRejectedFields(form) };
  }
  if (response.status === 404) {
    return { ok: false, code: ADDRESS_SERVICE_CODES.VALIDATION_FAILED, fields: buildAllRejectedFields(form) };
  }
  if (!response.ok) return { ok: false, code: ADDRESS_SERVICE_CODES.LOCATION_IQ_UNAVAILABLE };
  const hit = parseLocationIQSearchFirstHit(data);
  if (!hit) {
    return { ok: false, code: ADDRESS_SERVICE_CODES.VALIDATION_FAILED, fields: buildAllRejectedFields(form) };
  }
  const { fields, isValid } = validateGeocodeResultAgainstForm(hit, form);
  if (!isValid) return { ok: false, code: ADDRESS_SERVICE_CODES.VALIDATION_FAILED, fields };
  const normalizedForm = buildNormalizedFormFromFieldResults(form, fields);
  return { ok: true, hit, fields, normalizedForm, warnings: [] };
}

// UI

// UI constants
const CIVICO_TOOLTIP_PROPS = { enterTouchDelay: 0, disableInteractive: true };

function fingerprintAddressForm(f) {
  const o = { ...EMPTY_ADDRESS_FORM, ...f };
  return ADDRESS_FORM_FIELD_KEYS.map((k) => String(o[k] ?? "").trim()).join("\u001f");
}

const gridSx = {
  display: "grid",
  width: "100%",
  columnGap: { xs: 2, sm: 2 },
  rowGap: { xs: 2, sm: 2 },
  px: { xs: 4, sm: 0 },
  gridTemplateColumns: { xs: "repeat(5, minmax(0, 1fr))", sm: "repeat(7, minmax(0, 1fr))" },
};

const colSpan = {
  via: { xs: "span 5", sm: "span 4" },
  civico: { xs: "span 2", sm: "span 1" },
  comune: { xs: "span 3", sm: "span 2" },
  cap: { xs: "span 2", sm: "span 1" },
  provincia: { xs: "span 3", sm: "span 2" },
  regione: { xs: "span 3", sm: "span 2" },
  nazione: { xs: "span 2", sm: "span 2" },
};

const buttonPrimarySx = {
  width: 180,
  fontFamily: "Rubik, sans-serif",
  fontWeight: 700,
  textTransform: "none",
  borderRadius: "10px",
  px: 2.5,
  py: 1,
  backgroundColor: PRIMARY,
  boxShadow: `0 6px 20px ${alpha(PRIMARY, 0.1)}`,
  "&:hover": { backgroundColor: "#092b6a" },
};

const buttonOutlinedSx = {
  width: 180,
  fontFamily: "Rubik, sans-serif",
  fontWeight: 700,
  textTransform: "none",
  borderRadius: "10px",
  px: 2.5,
  py: 1,
  color: PRIMARY,
  borderColor: alpha(PRIMARY, 0.45),
  "&:hover": { borderColor: PRIMARY, backgroundColor: alpha(PRIMARY, 0.06) },
};

// UI AddressField component

function getFieldOutlineOverride(validated, fr, optional) {
  if (!validated || !fr?.filled) return {};
  if (fr.found) {
    return { "& .MuiOutlinedInput-root fieldset": { borderColor: alpha("#2e7d32", 0.5) } };
  }
  if (optional) {
    return { "& .MuiOutlinedInput-root fieldset": { borderColor: alpha("#ed6c02", 0.55) } };
  }
  return {};
}

function getFieldAdornment(loading, validated, fr, optional) {
  if (loading) {
    return <CircularProgress size={18} thickness={5} sx={{ color: PRIMARY }} />;
  }
  if (!validated || !fr?.filled) return null;
  if (fr.found) {
    return <CheckCircleOutline sx={{ fontSize: 20, color: "success.main" }} />;
  }
  if (optional) {
    return (
      <Tooltip title={CIVICO_UNVERIFIED_TOOLTIP_IT} arrow {...CIVICO_TOOLTIP_PROPS}>
        <span>
          <WarningAmber sx={{ fontSize: 20, color: "warning.main", cursor: "help", display: "block" }} />
        </span>
      </Tooltip>
    );
  }
  return <Close sx={{ fontSize: 20, color: "error.main" }} />;
}

function AddressField({
  id,
  fieldKey,
  label,
  labelTooltip,
  gridColumn,
  value,
  onChange,
  placeholder,
  fieldResult,
  loading,
}) {
  const fr = fieldResult;
  const validated = !!fr;
  const optional = OPTIONAL_CIVICO.has(fieldKey);
  const error = validated && !optional && (!fr.filled || !fr.found);
  const outlineOverride = getFieldOutlineOverride(validated, fr, optional);
  const adornment = getFieldAdornment(loading, validated, fr, optional);
  const fieldSx = {
    ...textFieldSx,
    ...outlineOverride,
    "& .MuiOutlinedInput-root": { ...textFieldSx["& .MuiOutlinedInput-root"], minWidth: 0 },
    "& .MuiFormHelperText-root": { display: "none" },
  };
  const labelEl = (
    <Typography
      component="label"
      htmlFor={id}
      sx={{
        fontFamily: "Rubik, sans-serif",
        fontWeight: 600,
        fontSize: "0.8125rem",
        color: PRIMARY,
        display: "block",
        mb: 0.75,
        textAlign: "center",
        cursor: labelTooltip ? "help" : undefined,
        textDecoration: labelTooltip ? "underline dotted" : undefined,
        textUnderlineOffset: labelTooltip ? 4 : undefined,
      }}
    >
      {label}
    </Typography>
  );
  return (
    <Box sx={{ gridColumn, minWidth: 0 }}>
      {labelTooltip ? (
        <Tooltip title={labelTooltip} arrow placement="top" {...CIVICO_TOOLTIP_PROPS}>
          <Box sx={{ display: "block", width: "100%" }}>{labelEl}</Box>
        </Tooltip>
      ) : (
        labelEl
      )}
      <TextField
        id={id}
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        error={error}
        variant="outlined"
        size="medium"
        sx={fieldSx}
        slotProps={{
          input: {
            endAdornment: adornment ? <InputAdornment position="end">{adornment}</InputAdornment> : null,
          },
        }}
      />
    </Box>
  );
}

AddressField.propTypes = {
  id: PropTypes.string.isRequired,
  fieldKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  labelTooltip: PropTypes.string,
  gridColumn: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  fieldResult: PropTypes.shape({
    filled: PropTypes.bool,
    found: PropTypes.bool,
    normalized: PropTypes.string,
    score: PropTypes.number,
    source: PropTypes.string,
  }),
  loading: PropTypes.bool,
};

// UI SedeAziendale component

export function SedeAziendale({
  enableValidation = false,
  value,
  onSedeFieldChange,
  hasStoredAddress = false,
  addressNeedsReverify = false,
  onVerifiedMerge,
  onResetToVerified,
  onGeocodeServiceUnavailable,
  apiKey = process.env.REACT_APP_LOCATIONIQ_API_KEY ?? "",
  language = "it",
}) {
  const form = value ?? EMPTY_ADDRESS_FORM;
  const [fieldRes, setFieldRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const pendingVerifiedMergeFingerprintRef = useRef(null);
  const retainedFieldResFingerprintRef = useRef(null);
  const addressFingerprint = fingerprintAddressForm(form);

  useEffect(() => {
    const fp = addressFingerprint;
    const pending = pendingVerifiedMergeFingerprintRef.current;
    if (pending != null && fp === pending) {
      retainedFieldResFingerprintRef.current = fp;
      pendingVerifiedMergeFingerprintRef.current = null;
      return;
    }
    if (retainedFieldResFingerprintRef.current === fp) return;
    retainedFieldResFingerprintRef.current = null;
    setFieldRes(null);
  }, [addressFingerprint]);

  const showVerifyReset = false;

  const run = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    setFieldRes(null);
    const result = await validateAddressWithLocationIQ(form, {
      apiKey,
      language,
      signal: abortRef.current.signal,
    });
    const serviceDown =
      result.ok === false &&
      (result.code === ADDRESS_SERVICE_CODES.MISSING_API_KEY ||
        result.code === ADDRESS_SERVICE_CODES.LOCATION_IQ_UNAVAILABLE);
    if (serviceDown) onGeocodeServiceUnavailable?.();
    if (result.code === ADDRESS_SERVICE_CODES.INCOMPLETE_FORM) {
      setLoading(false);
      const fields = {};
      ADDRESS_FORM_FIELD_KEYS.forEach(k => {
        if (k !== "regione" && !String(form[k] ?? "").trim()) {
          fields[k] = { filled: false, found: false, normalized: "", score: 0, source: "required" };
        }
      });
      setFieldRes(fields);
      return;
    }
    if (!result.ok) {
      setLoading(false);
      if (result.code === ADDRESS_SERVICE_CODES.ABORTED) return;
      if (result.code === ADDRESS_SERVICE_CODES.VALIDATION_FAILED && result.fields) setFieldRes(result.fields);
      return;
    }
    setFieldRes(result.fields);
    setLoading(false);
    pendingVerifiedMergeFingerprintRef.current = fingerprintAddressForm(result.normalizedForm);
    onVerifiedMerge?.({ ...result.normalizedForm });
  }, [apiKey, form, language, onGeocodeServiceUnavailable, onVerifiedMerge]);

  const setField = (sedeKey) => (val) => onSedeFieldChange?.(sedeKey, val);

  const handleReset = () => {
    setFieldRes(null);
    retainedFieldResFingerprintRef.current = null;
    pendingVerifiedMergeFingerprintRef.current = null;
    abortRef.current?.abort();
    onResetToVerified?.();
  };

  const handleKeyDown = (e) => {
    if (!showVerifyReset) return;
    if (e.key === "Enter") {
      e.preventDefault();
      run();
    }
  };

  useEffect(() => () => abortRef.current?.abort(), []);

  const canValidate = enableValidation && Boolean(String(apiKey ?? "").trim()) && !loading;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1000,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      onKeyDown={handleKeyDown}
    >
      <Box sx={gridSx}>
        <AddressField
          id="liqf-via"
          fieldKey="via"
          label="Via / Piazza"
          gridColumn={colSpan.via}
          placeholder="Leonardo da Vinci"
          value={form.via}
          onChange={setField("via")}
          fieldResult={fieldRes?.via}
          loading={loading}
        />
        <AddressField
          id="liqf-civico"
          fieldKey="civico"
          label="Civico"
          gridColumn={colSpan.civico}
          placeholder="14"
          value={form.civico}
          onChange={setField("civico")}
          fieldResult={fieldRes?.civico}
          loading={loading}
        />
        <AddressField
          id="liqf-comune"
          fieldKey="comune"
          label="Comune"
          gridColumn={colSpan.comune}
          placeholder="Milano"
          value={form.comune}
          onChange={setField("comune")}
          fieldResult={fieldRes?.comune}
          loading={loading}
        />
        <AddressField
          id="liqf-cap"
          fieldKey="cap"
          label="CAP"
          gridColumn={colSpan.cap}
          placeholder="20121"
          value={form.cap}
          onChange={setField("cap")}
          fieldResult={fieldRes?.cap}
          loading={loading}
        />
        <AddressField
          id="liqf-prov"
          fieldKey="provincia"
          label="Provincia"
          gridColumn={colSpan.provincia}
          placeholder="Milano"
          value={form.provincia}
          onChange={setField("provincia")}
          fieldResult={fieldRes?.provincia}
          loading={loading}
        />
        <AddressField
          id="liqf-reg"
          fieldKey="regione"
          label="Regione"
          labelTooltip={REGIONE_OPTIONAL_TOOLTIP_IT}
          gridColumn={colSpan.regione}
          placeholder="Lombardia"
          value={form.regione}
          onChange={setField("regione")}
          fieldResult={fieldRes?.regione}
          loading={loading}
        />
        <AddressField
          id="liqf-nazione"
          fieldKey="nazione"
          label="Nazione"
          gridColumn={colSpan.nazione}
          placeholder="Italia"
          value={form.nazione}
          onChange={setField("nazione")}
          fieldResult={fieldRes?.nazione}
          loading={loading}
        />
      </Box>

      {showVerifyReset ? (
        <>
          <Divider sx={{ my: 2.5, width: "100%" }} />
          <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ width: "100%" }}>
            <Button
              type="button"
              variant="contained"
              onClick={() => run()}
              disabled={!canValidate}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              sx={{ display: "flex", alignItems: "center", ...buttonPrimarySx }}
            >
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ display: "flex", alignItems: "center" }}>{loading ? "Validazione…" : "Verifica"}</span>
                <Typography
                  sx={{
                    fontFamily: "Rubik, sans-serif",
                    fontSize: "0.52rem",
                    color: "#ffffff",
                    lineHeight: 0.5,
                    display: "block",
                    textAlign: "left",
                    m: 0,
                  }}
                  component="span"
                >
                  <a
                    href="https://locationiq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#ffffff", textDecoration: "none" }}
                  >
                    by LocationIQ.com
                  </a>
                </Typography>
              </span>
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
              startIcon={<RestartAlt />}
              sx={buttonOutlinedSx}
            >
              Reset
            </Button>
          </Stack>
        </>
      ) : null}
    </Box>
  );
}

SedeAziendale.propTypes = {
  enableValidation: PropTypes.bool,
  value: PropTypes.shape({
    via: PropTypes.string,
    civico: PropTypes.string,
    comune: PropTypes.string,
    cap: PropTypes.string,
    provincia: PropTypes.string,
    regione: PropTypes.string,
    nazione: PropTypes.string,
  }),
  onSedeFieldChange: PropTypes.func,
  hasStoredAddress: PropTypes.bool,
  addressNeedsReverify: PropTypes.bool,
  onVerifiedMerge: PropTypes.func,
  onResetToVerified: PropTypes.func,
  onGeocodeServiceUnavailable: PropTypes.func,
  apiKey: PropTypes.string,
  language: PropTypes.string,
};

export default SedeAziendale;
 